import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import DataGrid, { Column, FormatterProps, EditorProps } from 'react-data-grid';
import { useTranslation } from 'react-i18next';
import { ImportRowError } from 'api/import-data/models/ImportDataParseResult';
import Select from 'common/components/select/Select';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import styles from './ImportEditableGrid.module.scss';

export interface ImportGridRow {
    rowNumber: number;
    errors: ImportRowError[];
    isValid: boolean;
    [key: string]: any;
}

interface Props<T extends ImportGridRow> {
    data: T[];
    columns: ColumnDefinition[];
    onRowChange: (rows: T[]) => void;
    onAddOption?: (value: string) => void;
}

export interface ColumnDefinition {
    key: string;
    name: string;
    editable?: boolean;
    width?: number;
    options?: string[];
    allowCustom?: boolean; // Allow typing custom values (for combobox)
    multiSelect?: boolean; // Use multi-select with checkboxes instead of single-select
}

function TextEditor<T extends ImportGridRow>({ row, column, onRowChange }: EditorProps<T>) {
    const value = row[column.key as keyof T];
    return (
        <input
            className={styles.editor}
            value={value as string ?? ''}
            onChange={e => onRowChange({ ...row, [column.key]: e.target.value })}
            autoFocus
        />
    );
}

function createSelectEditor<T extends ImportGridRow>(options: string[]) {
    return function SelectEditor({ row, column, onRowChange, onClose }: EditorProps<T>) {
        const value = row[column.key as keyof T] as string ?? '';

        return (
            <select
                className={styles.selectEditor}
                value={value}
                onChange={e => {
                    onRowChange({ ...row, [column.key]: e.target.value }, true);
                }}
                onBlur={() => onClose()}
                autoFocus
            >
                <option value=""></option>
                {options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                ))}
            </select>
        );
    };
}

function createComboboxEditor<T extends ImportGridRow>(options: string[], onAddOption?: (value: string) => void) {
    return function ComboboxEditor({ row, column, onRowChange, onClose }: EditorProps<T>) {
        const currentValue = row[column.key as keyof T] as string ?? '';
        const [inputValue, setInputValue] = useState(currentValue);
        const [showDropdown, setShowDropdown] = useState(true);
        const inputRef = useRef<HTMLInputElement>(null);
        const dropdownRef = useRef<HTMLDivElement>(null);

        // Filter options based on input
        const filteredOptions = useMemo(() => {
            if (!inputValue) return options;
            const lowerInput = inputValue.toLowerCase();
            return options.filter(opt => opt.toLowerCase().includes(lowerInput));
        }, [inputValue, options]);

        // Check if current input is a new value
        const isNewValue = inputValue && !options.some(opt => opt.toLowerCase() === inputValue.toLowerCase());

        useEffect(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, []);

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
        };

        const handleSelectOption = (option: string) => {
            setInputValue(option);
            onRowChange({ ...row, [column.key]: option }, true);
        };

        const handleBlur = (e: React.FocusEvent) => {
            // Check if focus moved to dropdown
            if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
                return;
            }

            // Save the value
            if (inputValue !== currentValue) {
                // If it's a new value, add it to options
                if (isNewValue && onAddOption) {
                    onAddOption(inputValue);
                }
                onRowChange({ ...row, [column.key]: inputValue }, true);
            }
            onClose();
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                if (isNewValue && onAddOption) {
                    onAddOption(inputValue);
                }
                onRowChange({ ...row, [column.key]: inputValue }, true);
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        return (
            <div className={styles.comboboxContainer}>
                <input
                    ref={inputRef}
                    className={styles.comboboxInput}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder=""
                />
                {showDropdown && (filteredOptions.length > 0 || isNewValue) && (
                    <div ref={dropdownRef} className={styles.comboboxDropdown}>
                        {isNewValue && (
                            <div
                                className={`${styles.comboboxOption} ${styles.comboboxOptionNew}`}
                                onMouseDown={() => handleSelectOption(inputValue)}
                            >
                                <i className="fas fa-plus" /> {inputValue}
                            </div>
                        )}
                        {filteredOptions.map(opt => (
                            <div
                                key={opt}
                                className={styles.comboboxOption}
                                onMouseDown={() => handleSelectOption(opt)}
                            >
                                {opt}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };
}

// Helper para normalizar string (trim + lowercase) para comparação
const normalizeForComparison = (value: string): string => value.trim().toLowerCase();

// Helper para encontrar a opção original pelo valor normalizado
const findOriginalOption = (value: string, options: string[]): string | null => {
    const normalized = normalizeForComparison(value);
    return options.find(opt => normalizeForComparison(opt) === normalized) || null;
};

// Multi-select editor that uses the existing Select component
function createMultiSelectEditor<T extends ImportGridRow>(
    options: string[],
    onAddOption?: (value: string) => void
) {
    return function MultiSelectEditor({ row, column, onRowChange, onClose }: EditorProps<T>) {
        const { t } = useTranslation();
        const currentValue = row[column.key as keyof T] as string ?? '';
        const containerRef = useRef<HTMLDivElement>(null);
        const [localOptions, setLocalOptions] = useState<string[]>(options);

        // Convert string options to SelectValueLabel format
        const selectOptions: SelectValueLabel[] = useMemo(() =>
            localOptions.map(opt => ({ value: opt, label: opt })),
            [localOptions]
        );

        // Parse current semicolon-separated value to SelectValueLabel array
        const selectedValues: SelectValueLabel[] = useMemo(() => {
            if (!currentValue) return [];
            const seen = new Set<string>();
            return currentValue.split(/[;,]/)
                .map(v => v.trim())
                .filter(v => {
                    if (!v || seen.has(v.toLowerCase())) return false;
                    seen.add(v.toLowerCase());
                    // Find matching option (case-insensitive)
                    const matchedOption = findOriginalOption(v, localOptions);
                    return true;
                })
                .map(v => {
                    const matchedOption = findOriginalOption(v, localOptions);
                    const label = matchedOption || v;
                    return { value: label, label };
                });
        }, [currentValue, localOptions]);

        // Handle selection change
        const handleChange = useCallback((newValue: SelectValueLabel[]) => {
            const stringValue = newValue.map(v => v.label).join(' ; ');
            onRowChange({ ...row, [column.key]: stringValue }, true);
        }, [onRowChange, row, column.key]);

        // Handle creating new option
        const handleCreateOption = useCallback((name: string) => {
            // Add to local options
            setLocalOptions(prev => [...prev, name]);
            // Add to global options via callback
            if (onAddOption) {
                onAddOption(name);
            }
            // Add to selection
            const newSelected = [...selectedValues, { value: name, label: name }];
            handleChange(newSelected);
        }, [selectedValues, handleChange, onAddOption]);

        // Close on click outside
        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                    onClose();
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [onClose]);

        return (
            <div ref={containerRef} className={styles.multiSelectContainer}>
                <Select
                    options={selectOptions}
                    value={selectedValues}
                    onChange={handleChange}
                    isMulti
                    isClearable
                    isCreatable={!!onAddOption}
                    onCreateOption={handleCreateOption}
                    createOptionText={t('common.add')}
                    placeholder=""
                    defaultMenuIsOpen={true}
                />
            </div>
        );
    };
}

function CellFormatter<T extends ImportGridRow>({ row, column }: FormatterProps<T>) {
    const value = row[column.key as keyof T];
    const error = row.errors?.find(e => e.column.toLowerCase() === column.key.toLowerCase());

    return (
        <div className={`${styles.cell} ${error ? styles.cellError : ''}`} title={error?.message}>
            {value != null ? String(value) : ''}
            {error && <span className={styles.errorIndicator}>!</span>}
        </div>
    );
}

// Ref to hold the inline select change handler (set inside ImportEditableGrid)
let inlineSelectChangeRef: ((rowNumber: number, columnKey: string, newValue: string) => void) | null = null;

function createSelectCellFormatter<T extends ImportGridRow>(options: string[]) {
    return function SelectCellFormatter({ row, column }: FormatterProps<T>) {
        const value = row[column.key as keyof T] as string ?? '';
        const error = row.errors?.find(e => e.column.toLowerCase() === column.key.toLowerCase());

        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            if (inlineSelectChangeRef) {
                inlineSelectChangeRef(row.rowNumber, column.key, e.target.value);
            }
        };

        return (
            <div className={`${styles.cell} ${styles.cellSelect} ${error ? styles.cellError : ''}`} title={error?.message}>
                <select
                    className={styles.inlineSelect}
                    value={value}
                    onChange={handleChange}
                >
                    <option value=""></option>
                    {options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                {error && <span className={styles.errorIndicator}>!</span>}
            </div>
        );
    };
}

function createMultiSelectCellFormatter<T extends ImportGridRow>(options: string[]) {
    return function MultiSelectCellFormatter({ row, column }: FormatterProps<T>) {
        const [isOpen, setIsOpen] = useState(false);
        const containerRef = useRef<HTMLDivElement>(null);
        const value = row[column.key as keyof T] as string ?? '';
        const error = row.errors?.find(e => e.column.toLowerCase() === column.key.toLowerCase());

        const selectedValues = useMemo(() => {
            if (!value) return new Set<string>();
            return new Set(value.split(/[;,]/).map(v => v.trim()).filter(Boolean));
        }, [value]);

        useEffect(() => {
            if (!isOpen) return;
            const handleClickOutside = (e: MouseEvent) => {
                if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [isOpen]);

        const toggleOption = (opt: string) => {
            const newSelected = new Set(selectedValues);
            if (newSelected.has(opt)) {
                newSelected.delete(opt);
            } else {
                newSelected.add(opt);
            }
            if (inlineSelectChangeRef) {
                inlineSelectChangeRef(row.rowNumber, column.key, Array.from(newSelected).join(' ; '));
            }
        };

        return (
            <div ref={containerRef} className={`${styles.cell} ${styles.cellSelect} ${error ? styles.cellError : ''}`} title={error?.message}>
                <div className={styles.inlineMultiSelect} onClick={() => setIsOpen(!isOpen)}>
                    <span className={styles.inlineMultiSelectValue}>{value || ''}</span>
                    <span className={styles.inlineMultiSelectArrow}><i className="fas fa-chevron-down" /></span>
                </div>
                {isOpen && (
                    <div className={styles.inlineMultiSelectDropdown}>
                        {options.map(opt => (
                            <label key={opt} className={styles.inlineMultiSelectOption} onMouseDown={e => e.preventDefault()}>
                                <input
                                    type="checkbox"
                                    checked={selectedValues.has(opt)}
                                    onChange={() => toggleOption(opt)}
                                />
                                <span>{opt}</span>
                            </label>
                        ))}
                    </div>
                )}
                {error && <span className={styles.errorIndicator}>!</span>}
            </div>
        );
    };
}

function ImportEditableGrid<T extends ImportGridRow>({ data, columns, onRowChange, onAddOption }: Props<T>) {
    const { t } = useTranslation();

    // Keep a ref to current data for inline select changes
    const dataRef = useRef(data);
    dataRef.current = data;

    // Set up the inline select change handler
    useEffect(() => {
        inlineSelectChangeRef = (rowNumber: number, columnKey: string, newValue: string) => {
            const newRows = dataRef.current.map(row =>
                row.rowNumber === rowNumber ? { ...row, [columnKey]: newValue } : row
            );
            onRowChange(newRows);
        };
        return () => { inlineSelectChangeRef = null; };
    }, [onRowChange]);

    const gridColumns: Column<T>[] = useMemo(() => {
        const cols: Column<T>[] = [
            {
                key: 'rowNumber',
                name: '#',
                width: 50,
                frozen: true,
                resizable: false,
                formatter: ({ row }) => (
                    <div className={`${styles.rowNumber} ${!row.isValid ? styles.rowError : ''}`}>
                        {row.rowNumber}
                        {!row.isValid && (
                            <span className={styles.rowErrorIcon} title={row.errors?.map(e => e.message).join(', ')}>
                                <i className="fas fa-exclamation-triangle" />
                            </span>
                        )}
                    </div>
                )
            }
        ];

        columns.forEach(col => {
            const hasOptions = col.options && col.options.length > 0;

            if (col.multiSelect && hasOptions) {
                // Inline multi-select with checkboxes
                cols.push({
                    key: col.key,
                    name: col.name,
                    width: col.width || 150,
                    resizable: true,
                    editable: false,
                    formatter: createMultiSelectCellFormatter(col.options!)
                });
            } else if (hasOptions) {
                // Inline single select (includes allowCustom columns)
                cols.push({
                    key: col.key,
                    name: col.name,
                    width: col.width || 150,
                    resizable: true,
                    editable: false,
                    formatter: createSelectCellFormatter(col.options!)
                });
            } else {
                // Text editor
                cols.push({
                    key: col.key,
                    name: col.name,
                    width: col.width || 150,
                    resizable: true,
                    editable: col.editable !== false,
                    editor: col.editable !== false ? TextEditor : undefined,
                    formatter: CellFormatter
                });
            }
        });

        return cols;
    }, [columns, onAddOption]);

    const handleRowsChange = useCallback((rows: T[]) => {
        onRowChange(rows);
    }, [onRowChange]);

    if (!data || data.length === 0) {
        return (
            <div className={styles.emptyMessage}>
                {t('import_data.no_data')}
            </div>
        );
    }

    return (
        <div className={styles.gridContainer}>
            <DataGrid
                className="rdg-light"
                columns={gridColumns}
                rows={data}
                onRowsChange={handleRowsChange}
                rowKeyGetter={row => row.rowNumber}
                defaultColumnOptions={{ resizable: true }}
            />
        </div>
    );
}

export default ImportEditableGrid;
